import * as React from 'react';
import type { Virtualizer } from '@tanstack/react-virtual';
import type { Omittable } from "../lib/props";

// Windowed virtual-scroll page manager: sizes a virtual list to the full server
// `total` while keeping only a small window of pages in memory. Shared engine
// behind the DataTable's windowed scroll and the Kanban column scrollbar. The
// provider's Apollo cache is per-page with no cross-page merge, so it hands the
// hook ONE page at a time (`pageItems` + `dataOffset`) and the hook assembles
// the window; evicted pages re-fetch on scroll. `getItem` returns undefined for
// an unloaded row (render a skeleton).

export type UseWindowedPagesOptions<TItem> = Omittable<{
  /** Full server total — the virtualizer's `count` upstream. */
  total: number;
  /** Page size; MUST match the provider's `limit`. */
  pageSize: number;
  /** The provider's CURRENT page (the rows for `dataOffset`). */
  pageItems: TItem[];
  /** Absolute offset of `pageItems[0]` within the full dataset. */
  dataOffset: number;
  /** Token that flips on filter/sort/search changes → resets the window. */
  variablesKey: string;
  /** Jump the provider to an absolute offset. Stable identity required. */
  fetchPage: (offset: number, limit: number) => void;
  /** The list's virtualizer, or null when disabled (collapsed). */
  virtualizer: Virtualizer<any, any> | null;
  /** Ref to the scrollable element (its `scrollTop` is reset on key change). */
  scrollElementRef: React.RefObject<HTMLElement | null>;
  /** Fold every "don't window" early-return into this. Default true. */
  enabled?: boolean;
  /** Return true to exempt a cached page from eviction (e.g. selected rows). */
  keepPage?: (pageStart: number, pageItems: TItem[]) => boolean;
}>;

export type UseWindowedPagesResult<TItem> = {
  /** Loaded row at an absolute index, or undefined (render a skeleton). */
  getItem: (index: number) => TItem | undefined;
  /** Whether the row at an absolute index is loaded. */
  isLoaded: (index: number) => boolean;
  /** Bumps on every cache mutation → drives the consumer's re-render. */
  cacheVersion: number;
};

export function useWindowedPages<TItem>({
  total,
  pageSize,
  pageItems,
  dataOffset,
  variablesKey,
  fetchPage,
  virtualizer,
  scrollElementRef,
  enabled = true,
  keepPage,
}: UseWindowedPagesOptions<TItem>): UseWindowedPagesResult<TItem> {
  const PAGE_SIZE = pageSize;

  const fetchPageRef = React.useRef(fetchPage);
  fetchPageRef.current = fetchPage;
  const keepPageRef = React.useRef(keepPage);
  keepPageRef.current = keepPage;

  const pageCacheRef = React.useRef(new Map<number, TItem[]>());
  const pendingPagesRef = React.useRef(new Set<number>());

  // Clear the page cache on data-context change, keyed on `variablesKey` not
  // `pageItems` identity: per-page fetching emits a transient empty array
  // between skip transitions that would otherwise wipe the cache mid-scroll and
  // snap scrollTop to 0. Runs in RENDER so the same render's `getItem` reads the
  // cleared cache.
  const prevVariablesKeyRef = React.useRef(variablesKey);
  if (
    prevVariablesKeyRef.current !== variablesKey &&
    pageCacheRef.current.size > 0
  ) {
    pageCacheRef.current.clear();
    pendingPagesRef.current.clear();
    const el = scrollElementRef.current;
    if (el) el.scrollTop = 0;
  }
  prevVariablesKeyRef.current = variablesKey;

  // After a debounced (distant-jump) fetch fires, blocks `fetchPageImmediate`
  // from overriding it — else a late/momentum scroll event cancels the query for
  // the page where the user actually stopped.
  const debounceFetchingRef = React.useRef(false);

  // Bump after cache mutations so the consumer re-renders against the fresh cache
  // and the checkPages effect re-fires to load adjacent visible pages.
  const [cacheVersion, setCacheVersion] = React.useState(0);

  React.useEffect(() => {
    // Always clear the debounce lock on a provider response, even with 0 rows
    // (stale total beyond actual data) — else an empty fetch permanently blocks
    // fetchPageImmediate. Also overwrites the page at `pageStart`, which the
    // deep-page-staleness self-heal relies on.
    debounceFetchingRef.current = false;
    if (pageItems.length > 0) {
      // Store the current page under its absolute offset so `getItem` resolves.
      const pageStart = Math.floor(dataOffset / PAGE_SIZE) * PAGE_SIZE;
      pageCacheRef.current.set(pageStart, pageItems);
      pendingPagesRef.current.delete(pageStart);
      setCacheVersion((v) => v + 1);
    }
  }, [pageItems, dataOffset, PAGE_SIZE]);

  // Debounced fetchPage for distant jumps (fast scrolling / scrollbar drag).
  const fetchPageTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const debouncedFetchPage = React.useCallback(
    (offset: number) => {
      if (fetchPageTimerRef.current !== null) {
        clearTimeout(fetchPageTimerRef.current);
      }
      fetchPageTimerRef.current = setTimeout(() => {
        fetchPageTimerRef.current = null;
        debounceFetchingRef.current = true;
        // Clear stale pending entries from intermediate scroll positions whose
        // queries were canceled when the offset was overwritten.
        pendingPagesRef.current.clear();
        pendingPagesRef.current.add(offset);
        fetchPageRef.current?.(offset, PAGE_SIZE);
      }, 300);
    },
    [PAGE_SIZE],
  );
  React.useEffect(
    () => () => {
      if (fetchPageTimerRef.current !== null)
        clearTimeout(fetchPageTimerRef.current);
    },
    [],
  );

  // Immediate fetch for adjacent pages (no debounce — boundary is visible).
  // Clear stale pending entries first; the provider serves one page at a time,
  // so a page left "pending" by a previous overridden request would never load.
  const fetchPageImmediate = React.useCallback(
    (offset: number) => {
      if (pageCacheRef.current.has(offset)) return;
      if (debounceFetchingRef.current) return;
      pendingPagesRef.current.clear();
      pendingPagesRef.current.add(offset);
      fetchPageRef.current?.(offset, PAGE_SIZE);
    },
    [PAGE_SIZE],
  );

  // Scroll-driven page fetching: request visible pages, evict off-screen ones.
  // Listener fires on actual scrolls + key dep changes, not every render.
  React.useEffect(() => {
    if (!enabled || !virtualizer) return;

    const checkPages = () => {
      if (total === 0) return;
      const items = virtualizer.getVirtualItems();
      // Destructuring the ends is also the empty-window check: no first item
      // means nothing is on screen to page for.
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) return;

      const firstVisible = first.index;
      const lastVisible = last.index;
      const firstPage = Math.floor(firstVisible / PAGE_SIZE) * PAGE_SIZE;
      const lastPage = Math.floor(lastVisible / PAGE_SIZE) * PAGE_SIZE;

      // Request any visible page not yet cached or pending.
      for (let p = firstPage; p <= lastPage; p += PAGE_SIZE) {
        if (!pageCacheRef.current.has(p) && !pendingPagesRef.current.has(p)) {
          // Adjacent to a cached page → fetch immediately.
          // Distant (fast scroll) → debounce.
          const isAdjacent =
            pageCacheRef.current.has(p - PAGE_SIZE) ||
            pageCacheRef.current.has(p + PAGE_SIZE);
          if (isAdjacent || pageCacheRef.current.size === 0) {
            fetchPageImmediate(p);
          } else {
            debouncedFetchPage(p);
          }
        }
      }

      // Evict pages outside the visible range + 1 page buffer, but keep pages
      // the consumer exempts (e.g. those holding selected rows).
      for (const key of pageCacheRef.current.keys()) {
        if (key < firstPage - PAGE_SIZE || key > lastPage + PAGE_SIZE) {
          const exempt = keepPageRef.current;
          if (exempt) {
            const cached = pageCacheRef.current.get(key);
            if (cached && exempt(key, cached)) continue;
          }
          pageCacheRef.current.delete(key);
        }
      }
    };

    // Check immediately for initial state / dep changes.
    checkPages();

    const scrollEl = scrollElementRef.current;
    if (!scrollEl) return;
    scrollEl.addEventListener('scroll', checkPages, { passive: true });
    return () => scrollEl.removeEventListener('scroll', checkPages);
    // cacheVersion: re-check after a page loads so adjacent visible pages get
    // fetched.
     
  }, [
    enabled,
    virtualizer,
    total,
    PAGE_SIZE,
    fetchPageImmediate,
    debouncedFetchPage,
    scrollElementRef,
    cacheVersion,
  ]);

  const getItem = React.useCallback(
    (index: number): TItem | undefined => {
      const pageStart = Math.floor(index / PAGE_SIZE) * PAGE_SIZE;
      const pageData = pageCacheRef.current.get(pageStart);
      return pageData ? pageData[index - pageStart] : undefined;
    },
    [PAGE_SIZE],
  );

  const isLoaded = React.useCallback(
    (index: number): boolean => getItem(index) !== undefined,
    [getItem],
  );

  return { getItem, isLoaded, cacheVersion };
}
